/*
 * This is where all the common, public js functions will live.
 * This way public functions are accessible from any of the other js modules
 * Please do not encapsulate anything in here as it will result in many files
 * ceasing to work with no clear cause.
 */

import addMinutes from 'date-fns/add_minutes'
import isDateWithinRange from 'date-fns/is_within_range'
import concat from 'lodash/concat'
import filter from 'lodash/filter'
import flowRight from 'lodash/flowRight'
import formatDate from 'date-fns/format'
import get from 'lodash/get'
import getYear from 'date-fns/get_year'
import isEmpty from 'lodash/isEmpty'
import isFinite from 'lodash/isFinite'
import isNil from 'lodash/isNil'
import isNumber from 'lodash/isNumber'
import isPlainObject from 'lodash/isPlainObject'
import isString from 'lodash/isString'
import parseDate from 'date-fns/parse'
import partial from 'lodash/partial'
import partialRight from 'lodash/partialRight'
import reduce from 'lodash/reduce'
import size from 'lodash/size'
import startOfDay from 'date-fns/start_of_day'
import uniqBy from 'lodash/uniqBy'
import {clean} from '../types/Height'

import {
  FIT_TYPE_LABEL_MAP,
  STANDARD_DATE_FORMAT,
  TRANSACTION_TYPE_BUY,
  TRANSACTION_TYPE_RENT,
} from '../const'

// eslint-disable-next-line max-len
export const isEmailAddress = (x) =>
  /^[-a-z0-9~!$%^&*_=+}{'?]+(\.[-a-z0-9~!$%^&*_=+}{'?]+)*@([a-z0-9_][-a-z0-9_]*(\.[-a-z0-9_]+)*\.(aero|arpa|biz|com|coop|edu|email|gov|info|int|mil|museum|name|net|org|pro|travel|mobi|[a-z][a-z])|([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}))(:[0-9]{1,5})?$/i.test(
    x,
  )

// eslint-disable-next-line max-len
export const isTenDigitPhoneNumber = (x) =>
  /^[(]{0,1}[0-9]{3}[)]{0,1}[-\s.]{0,1}[0-9]{3}[-\s.]{0,1}[0-9]{4}$/.test(x)

export const isValidFirstDigitPhoneNumber = (number) => /^[2-9]{1}[0-9]{9}$/.test(number)

export const isValidAddressLength = (value) => value.length <= 25

/*
 * Used for sending prices to GTM. Matches our Python implementation of
 * Product.formatted_price. Use the tests to see edge cases.
 */
export const formatPrice = (price) => {
  const [whole, decimal] = `${price}`.split('.')
  const decimalIsZero = decimal ? !!decimal.match(/^0+$/) : true
  if (decimalIsZero) {
    return whole
  }
  const paddedDecimal = `${decimal}0`.slice(0, 2)
  return `${whole}.${paddedDecimal}`
}

export const formatPromoDiscountPrice = (price) => {
  const discountAmount = 0.2 * price
  const discountPrice = Math.round(price - discountAmount)

  return discountPrice ? formatPrice(discountPrice) : ''
}
/*
 * Used to convert a string or number into a dollar format with decimal places
 * Remove dollar signs, and commas for string values > 1000
 */
export const stripDollarSigns = (amount) => {
  if (!isString(amount)) return amount
  return amount.replace('$', '').replace(',', '')
}

/*
 * Used to reformat a string containing negative numbers into a positive number
 * with parenthesis around the number. This is an accounting convention for
 * displaying negative numbers. i.e. $-1.00 becomes ($1.00)
 */
export const getNegativeAmountFormat = (amount) => {
  // validates string doesn't already have parenthesis, otherwise returns original
  if (!isString(amount) || !amount.search(/\((.*)\)/)) return amount
  return `(${amount.replace('-', '')})`
}

export const getDecimalDollarFormat = (amount, places, negativeAmountFormatting = false) =>
  ((value) => {
    const shouldFormat = (isNumber(value) || isString(value)) && isFinite(+value)
    // format with dollar sign, to given places, and add commas for values > 1000
    const num = shouldFormat
      ? `$${(+value)
          .toFixed(places)
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
      : value
    return negativeAmountFormatting ? getNegativeAmountFormat(num) : num
  })(stripDollarSigns(amount))

export const formatTimeSlot = (timeSlotUtc) =>
  flowRight(
    partialRight(formatDate, 'h:mm A'),
    partialRight(addMinutes, timeSlotUtc),
    startOfDay,
  )(new Date())

/*
 * Evaluates a predicate function at a given (or default) polling interval.
 * When the predicate returns true, executes the passed function. Executes
 * the passed function immediately if the predicate is already true. Similar
 * ideas are available in the "async" library: https://github.com/caolan/async
 */
export const pollAndThen = (predicate, fn, interval = 800) => {
  const loop = () => (predicate() ? fn() : setTimeout(loop, interval))
  loop()
}

// Works like pollAndThen but returns a promise instead of accepting a callback
export const pollAndThenPromise = (predicate, interval) =>
  new Promise((resolve) => pollAndThen(predicate, resolve, interval))

/**
 * Onboarding fetch wrapper
 *
 * @param url
 * @param payload
 * @param successCallback
 * @param errorCallback
 */
export function postData(url, payload, successCallback, errorCallback) {
  fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
    .then((response) => {
      if (response.ok) {
        response.json().then((jsonResponse) => {
          successCallback(jsonResponse)
        })
      } else if (response.status === 403) {
        console.warn(response.statusText, 'CSRF Token Invalid')
        errorCallback()
      } else {
        console.error(response.statusText)
        errorCallback()
      }
    })
    .catch((error) => console.warn(`Not able to save information. Network error: ${error}`))
}

export const getFromResponse = (response, contentType, functionName) => {
  let contentTypeHeader
  let result = null

  if (response && response.ok) {
    contentTypeHeader = response.headers.get('content-type')

    if (contentTypeHeader && contentTypeHeader.includes(contentType)) {
      result = response[functionName]()
    }
  }

  return result
}

export const getJsonFromResponse = (response) =>
  getFromResponse(response, 'application/json', 'json')
export const getSvgFromResponse = (response) => getFromResponse(response, 'image/svg+xml', 'text')

/**
 * Inserts a `<script>` tag into the `<head>` of the document. Returns a Promise
 * that is resolved on load or rejected on error.
 *
 * @param {String} url The URL of the script you want to load
 * @return {Promise}
 */
export const loadScript = (url) =>
  new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.async = true
    script.defer = true
    script.onload = resolve
    script.onerror = partial(reject, `Failed to load ${url}`)
    script.src = url

    const prior = document.getElementsByTagName('script')[0]
    if (prior && prior.parentNode) {
      prior.parentNode.insertBefore(script, prior)
    }
  })

export const hasLength = (x) => get(x, 'length') > 0

export const isEmptyString = (string) => isString(string) && !size(string)
export const isFilledString = (string) => isString(string) && !!size(string)

export const handleImageLoaded = () => {
  // Use as a callback for onLoad on images when using nuka carousel
  // because nuka carousel sometime renders the page with the image container at height 0
  const event = document.createEvent('Event')
  event.initEvent('resize', true, true)
  window.addEventListener('resize', (e) => e.preventDefault(), false)
  // More verbose approach to window dispatching event. Works for IE as well now.
  window.dispatchEvent(event)
}

export const and = (...args) => args.reduce((x, y) => x && y)
export const or = (...args) => args.reduce((x, y) => x || y)
export const not = (x) => !x

export const rtrim = (string = '', fragment = '') =>
  string.substr(string.length - fragment.length, fragment.length) === fragment
    ? rtrim(string.substr(0, string.length - fragment.length), fragment)
    : string

export const notFn =
  (fn) =>
  (...args) =>
    !fn(...args)

export const formatDateString = (dateString, formatString) =>
  formatDate(parseDate(dateString), formatString)
export const stringToJsDate = (string) => (string ? parseDate(string) : new Date())
export const jsDateToString = (date) => formatDateString(date, STANDARD_DATE_FORMAT)
export const formatSqlDate = (date) => {
  if (!date) return ''
  const [year, month, day] = date.split('-')

  return `${month}/${day}/${year}`
}

/**
 * Helper function to determine if a value is a string or numeric 0.
 *
 * @param {*} value Value to check
 * @returns {Boolean} If it is 0
 */
export const isZero = (value) => (isFinite(value) || isFilledString(value)) && Number(value) === 0

/**
 * Creates Date object based on UTC from input date string.
 *
 * @param dateString String - String representation of date to be formatted.
 * @returns {Date} Date object with UTC time representation of input date string.
 */
export const getUtcDate = (dateString = Date.now()) => {
  const date = new Date(dateString)

  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
  )
}

export const noop = () => {}

export const noDecorators = {
  renderCenterLeftControls: noop,
  renderCenterRightControls: noop,
  renderBottomCenterControls: noop,
}

// converts a string to a number if that string is a finite number
// otherwise, passes the value through
// ex.
//   1. undefined => undefined
//   2. null => null
//   3. '' => ''
//   4. '12.' => '12.'
//   5. '12' => '12'
// (`+` converts value to number)
export const convertNumberStringToNumber = (val) =>
  isFilledString(val) && isFinite(+val) ? +val : val

export function renameKeys(obj, renameMap) {
  return reduce(
    obj,
    (acc, value, key) => ({
      ...acc,
      [renameMap[key] || key]: isPlainObject(value) ? renameKeys(value, renameMap) : value,
    }),
    {},
  )
}

// returns a new array of items unique by transactionType and an identifier (productSetId, slug)
export const concatUniqueItems = (products, identifier) => {
  const buyItems = filter(products, {transactionType: TRANSACTION_TYPE_BUY})
  const rentItems = filter(products, {transactionType: TRANSACTION_TYPE_RENT})
  const uniqueBuy = uniqBy(buyItems, identifier)
  const uniqueRent = uniqBy(rentItems, identifier)
  return concat(uniqueBuy, uniqueRent)
}

/**
 * Helper function to get the quantity of a product by slug and transaction type
 * @return {Number} Returns the number of products that match the given slug and transaction type
 */
export const getQtyNum = (products, slug, transactionType) =>
  filter(products, {slug, transactionType}).length

/**
 * Helper function to transform an array of objects to an object containing arrays, keyed by the common properties.
 * This assumes that the array of objects have the same set of properties.
 *
 * Input:
 * [{ size: 48,length: "L"}, {size: 42, length: "R'}]
 *
 * Output:
 * { size: [48, 42], length: ["L", "R"] }
 *
 * @param {Array} array Array of objects with common set of properties.
 * @param {Object} initValue Initial object value to seed reducer
 * @returns {Object} Reduced object containing arrays keyed by the common properties between each object.
 */
export const reduceArrayOfObjectsToObject = (array, initValue = {}) =>
  array.reduce(
    (output, object) =>
      Object.keys(object).reduce(
        (properties, propertyName) => ({
          ...properties,
          [propertyName]: [...get(properties, `${propertyName}`, []), object[propertyName]],
        }),
        {...output},
      ),
    {...initValue},
  )

/**
 * Helper function to find the next largest value in an array compared against the input target value. If a value
 * is not found, then the index of the last element of the array is returned.
 *
 * @param {Number[]} array Array of integer values
 * @param {Number} target Relative integer value to lookup next largest value
 * @returns {number} Index of next largest value
 */
export const findNextLargestValueIndex = (array, target) => {
  const sortedArr = [...array].sort((a, b) => a - b)

  for (let i = 0; i < sortedArr.length; i += 1) {
    if (sortedArr[i] > target) {
      return i
    }
  }
  return sortedArr.length - 1
}

/**
 * Helper function to create formatted jacket size string label.
 *
 * @param {Object} sizeConfig Config containing values of jacket dimensions
 * @param {Number} sizeConfig.chest Value of jacket's body size
 * @param {Number} sizeConfig.jacketLength Value of jacket length
 * @returns {String} Formatted jacket size label (eg: "42R")
 */
export const formatJacketSizeLabel = ({chest, jacketLength}) =>
  chest && jacketLength && `${chest}${jacketLength}`

/**
 * Helper function to create formatted pants size string label.
 *
 * @param {Object} sizeConfig Config containing values of pants dimensions
 * @param {Number} sizeConfig.inseam Value of pants' inseam length
 * @param {String} sizeConfig.pantFit Value of pants' fit type ('C', 'S' or 'XS')
 * @param {Number} sizeConfig.waist Value of pants' waist circumference
 * @returns {String} Formatted pants size label (eg: "34/32 Classic")
 */
export const formatPantsSizeLabel = ({inseam, pantFit, waist}) =>
  waist && inseam && pantFit && `${waist}/${inseam} ${FIT_TYPE_LABEL_MAP[pantFit]}`

/**
 * Helper function to create formatted shirt size string label.
 *
 * @param {Object} sizeConfig Config containing values of shirt dimensions
 * @param {Number} sizeConfig.neck Value of shirt's neck circumference
 * @param {String} sizeConfig.shirtFit Value of shirt's fit type ('C', 'S' or 'XS')
 * @param {Number} sizeConfig.shirtSleeve Value of shirt's sleeve length
 * @returns {String} Formatted shirt size label (eg: "16/32 Slim")
 */
export const formatShirtsSizeLabel = ({neck, shirtFit, shirtSleeve}) =>
  neck && shirtSleeve && shirtFit && `${neck}/${shirtSleeve} ${FIT_TYPE_LABEL_MAP[shirtFit]}`

/**
 * Helper function to create formatted vest size string label.
 *
 * @param {Object} sizeConfig Config containing values of vest dimensions
 * @param {String} sizeConfig.vest Value of vest's body size (eg: 'XS', 'S', 'M', 'XXL')
 * @param {String} sizeConfig.vestLength Value of vest's length (eg: 'R', 'L')
 * @returns {String} Formatted vest size label (eg: "XSR")
 */
export const formatVestSizeLabel = ({vest, vestLength = ''}) => vest && `${vest}${vestLength}`

/**
 * Helper function that formats an item's size config into the conventional sizing display format based on the item's
 * product category type. We should strive to reuse this same size formatting across the application to maintain
 * consistency.
 *
 * @param {Object} sizeConfig Config object containing values of the item's dimensions
 * @param {String} productCategory Item's garment category (eg: 'jackets', 'pants')
 * @returns {String} Combined string representation of an item's sizing dimensions
 */
export const formatProductSizeLabelByCategory = (sizeConfig, productCategory) => {
  if (isNil(sizeConfig) && isEmpty(sizeConfig)) {
    return 'TBD'
  }

  let productSizeLabel

  switch (productCategory) {
    case 'jackets':
      productSizeLabel = formatJacketSizeLabel(sizeConfig)
      break

    case 'pants':
      productSizeLabel = formatPantsSizeLabel(sizeConfig)
      break

    case 'shirts':
      productSizeLabel = formatShirtsSizeLabel(sizeConfig)
      break

    case 'vests':
      productSizeLabel = formatVestSizeLabel(sizeConfig)
      break

    case 'shoes':
      productSizeLabel = sizeConfig.shoe && `${sizeConfig.shoe}`
      break

    case 'belts':
      productSizeLabel = sizeConfig.belt && `${sizeConfig.belt}`
      break

    default:
      // check if product has only one size
      if (sizeConfig.oneSize) {
        return 'One Size'
      }

      break
  }

  return productSizeLabel || 'TBD'
}

/**
 * The Fastly equivalent to Akamai's DCThumbnail image policy.
 *
 * We should strive to use image processing query strings that are better
 * tailored to the implementation than relying on old, readily available crops.
 * This is solely meant for an easy transition.
 */
export const fastlyImpolicyDCThumbnail = '?trim=0,186&width=461'

/**
 * The Fastly equivalent to Akamai's PDPdesktop image policy.
 */
export const fastlyImpolicyPDPdesktop = '?width=845&height=875'

/**
 * Utility to surface sizing information for products based on category slug.
 * Currently, this is an MVP since our sizing information is the same across
 * categories and ToolTime has existing bugs that make it difficult to update
 * product information.
 *
 * @param {String} productCategorySlug The product's category slug
 * @returns {String[]} Sizing details in an array
 * @todo Move this information into the Product Service
 */
export const getSizingInfoByProductCategory = (productCategorySlug) => {
  switch (productCategorySlug) {
    case 'tuxedos':
    case 'suits':
      return [
        'Jacket: 34–58. Extra short to extra long lengths available.',
        'Pants: Waist 28–54, inseam 26–36. Slim or classic fit.',
      ]
    case 'shirts':
      return ['Collar 14–24, sleeve 31–39 (odd sizes only). Slim or classic fit.']
    case 'shoes':
      return ['Men’s 6–16 (whole sizes only).']
    case 'vests':
      return ['XS–6XL. Regular and long lengths available.']
    default:
      return ['One size.']
  }
}

/**
 * Converts a string representing hours in 24-hour format to 12-hour format.
 *
 * @param {string} timeString in the format of HH:MM:SS (with HH in 24-hour notation)
 * @returns {string} displayTime in the format of HH:MM AM or HH:MM PM (with HH in 12-hour notation)
 */
export const format12HourString = (timeString) => {
  const date = new Date(`January 01, 1970 ${timeString}`)
  const options = {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  }
  const displayTime = date.toLocaleString('en-US', options)
  return displayTime
}

/**
 * Creates an array with feet and inches values.
 * @returns {number[]}
 */
export function convertInchToFeetAndInches(inches) {
  const ft = Math.floor(clean(inches) / 12)
  const inch = clean(inches % 12)
  return [ft, inch]
}

/**
 * Creates an array of height options.
 * @returns {string[]} List of height options
 */
export function setHeightRange(startInch, endInch) {
  const heightRange = []
  for (let index = startInch; index <= endInch; index += 1) {
    const [ft, inch] = convertInchToFeetAndInches(index)
    heightRange.push(`${ft}'${inch}"`)
  }
  return heightRange
}

/**
 * Creates an array of birth year options.
 * @returns {string[]} List of birth year options
 */
export function setBirthYearRange(endYear) {
  const birthYearRange = []
  for (let index = getYear(new Date()); index >= endYear; index -= 1) {
    birthYearRange.push(`${index}`)
  }
  return birthYearRange
}

/**
 * Checks if the `selectedDate` falls within the `dateRange`
 * @param {String} selectedDate A date-fns formatted date string
 * @param {Object} dateRange An object with `start_date` and `end_date` properties
 * @return {Boolean} Returns true if selectedDate falls within the `dateRange`, otherwise false
 */
export function isSoldOutForDate(selectedDate, dateRange) {
  const {start_date: startDate, end_date: endDate} = dateRange
  return isDateWithinRange(selectedDate, parseDate(startDate), parseDate(endDate))
}

/**
 * Checks if the `selectedDate` falls within one of the soldout `dateRanges`
 * @param {String} selectedDate A date-fns formatted date string
 * @param {Array} dateRanges An array of objects with `start_date` and `end_date` properties
 * @return {Boolean} Returns true if the selectedDate falls within one of the dateRanges, otherwise false
 */
export function isItemSoldOut(selectedDate, dateRanges) {
  const dates = dateRanges.filter((dateRange) => isSoldOutForDate(selectedDate, dateRange))
  return !!dates.length
}

/**
 * Adds a global class to the main html tag
 * @param {String} className
 */
export function addGlobalHtmlClass(className) {
  window.document.documentElement.classList.add(className)
}

/**
 * Removes a global class from the main html tag
 * @param {String} className
 */
export function removeGlobalHtmlClass(className) {
  window.document.documentElement.classList.remove(className)
}
